import { useState } from "react";

interface Props {
  title: string;
  description: string;
  confirmLabel: string;
  confirmDanger?: boolean;
  /** When set, user must type this exact text before the confirm button becomes clickable */
  confirmText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  title,
  description,
  confirmLabel,
  confirmDanger = false,
  confirmText,
  onConfirm,
  onCancel,
}: Props) {
  const [typedValue, setTypedValue] = useState("");
  const requiresTyping = !!confirmText && confirmText.length > 0;
  const canConfirm = !requiresTyping || typedValue.trim() === confirmText;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.3)" }}
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl w-full max-w-sm mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon + Title */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{
                backgroundColor: confirmDanger ? "rgba(220,38,38,0.08)" : "rgba(131,0,81,0.08)",
              }}
            >
              <i
                className={`text-base ${confirmDanger ? "ri-delete-bin-line" : "ri-question-line"}`}
                style={{ color: confirmDanger ? "#dc2626" : "#830051" }}
              ></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 leading-snug">{title}</h3>
              <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{description}</p>
            </div>
          </div>
        </div>

        {/* Type-to-confirm input (only when confirmText is provided) */}
        {requiresTyping && (
          <div className="px-6 pb-4">
            <label className="block text-[11px] font-medium text-gray-500 mb-1.5">
              Type <span className="text-gray-800 font-semibold select-all">{confirmText}</span> to confirm:
            </label>
            <input
              type="text"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder={confirmText}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-red-300 transition-colors"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && canConfirm) {
                  e.preventDefault();
                  onConfirm();
                }
              }}
            />
          </div>
        )}

        {/* Divider */}
        <div className="h-px bg-gray-100 mx-6" />

        {/* Actions */}
        <div className="flex items-center justify-end gap-2.5 px-6 py-4">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-sm text-gray-600 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            className="px-4 py-1.5 text-sm font-medium text-white rounded-lg cursor-pointer whitespace-nowrap transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:opacity-90"
            style={{ backgroundColor: confirmDanger ? "#dc2626" : "#830051" }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}